import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import useEntityPanelState from './hooks/useEntityPanelState';
import { LAYOUT_MODES } from './constants';
import {
  PanelSearch,
  FilterDropdown,
  PanelArrow,
  CheckboxItem,
  CategoryItem,
  SelectedItem,
  NoDataItem,
} from './components';
import * as styles from './EntityPanelSelector.module.css';

// Re-export LAYOUT_MODES for external use
export { LAYOUT_MODES };

/**
 * EntityPanelSelector Component
 * A flexible panel selector component supporting 3 layout modes:
 * 
 * Mode 1: TWO_COLUMNS - Simple 2-panel layout without filter dropdown
 *         Used by: HostGroupFilter, BusinessServiceFilter
 * 
 * Mode 2: TWO_COLUMNS_WITH_FILTER - 2-panel layout with filter dropdown
 *         Used by: HostFilter "By List" mode
 * 
 * Mode 3: THREE_COLUMNS_WITH_FILTER - 3-panel layout with filter dropdown
 *         Used by: HostFilter "By Host Group" mode, MetricFilter
 */

function EntityPanelSelector({
  mode,
  availableItems,
  selectedItemIds,
  onSelectionChange,
  middlePanelItems,
  middlePanelLoading,
  categories,
  selectedCategoryId,
  onCategorySelect,
  selectedItemsWithCategory,
  filterOptions,
  selectedFilterValue,
  onFilterChange,
  labels,
  valueField,
}) {
  const { t } = useTranslation();
  
  // Use custom hook for state management
  const {
    instanceId,
    leftSearchTerm,
    setLeftSearchTerm,
    middleSearchTerm,
    setMiddleSearchTerm,
    selectedSearchTerm,
    setSelectedSearchTerm,
    getItemValue,
    filteredLeftPanelItems,
    filteredMiddlePanelItems,
    groupedMiddlePanelItems,
    filteredSelectedItems,
    groupedSelectedItems,
    handleItemToggle,
    handleSelectAll,
    handleClearAll,
    handleRemoveItem,
  } = useEntityPanelState({
    mode,
    availableItems,
    selectedItemIds,
    onSelectionChange,
    middlePanelItems,
    categories,
    selectedItemsWithCategory,
    valueField,
  });

  // Check if select all should be shown
  const showSelectAll = mode === LAYOUT_MODES.THREE_COLUMNS_WITH_FILTER
    ? selectedCategoryId && filteredMiddlePanelItems.length > 0
    : filteredLeftPanelItems.length > 0;

  // Render grouped selected items
  const renderGroupedSelectedItems = () => {
    if (!groupedSelectedItems || groupedSelectedItems.length === 0) {
      return <NoDataItem message={labels.noItemsSelected || t('reports.filters.noEntitiesSelected')} />;
    }

    return groupedSelectedItems.map((group) => (
      <li key={group.categoryId} className={styles.categoryGroup}>
        <div className={styles.categoryGroupHeader}>{group.categoryName}</div>
        <ul className={styles.categoryGroupItems}>
          {group.items.map((item) => (
            <SelectedItem
              key={getItemValue(item)}
              item={item}
              getItemValue={getItemValue}
              onRemove={handleRemoveItem}
            />
          ))}
        </ul>
      </li>
    ));
  };

  // Render selected panel content
  const renderSelectedPanelContent = () => {
    if (groupedSelectedItems) {
      return renderGroupedSelectedItems();
    }
    
    if (filteredSelectedItems.length === 0) {
      return <NoDataItem message={labels.noItemsSelected || t('reports.filters.noEntitiesSelected')} />;
    }
    
    return filteredSelectedItems.map((item) => (
      <SelectedItem
        key={getItemValue(item)}
        item={item}
        getItemValue={getItemValue}
        onRemove={handleRemoveItem}
      />
    ));
  };

  // Render middle panel content (for 3-column mode)
  const renderMiddlePanelContent = () => {
    if (!selectedCategoryId) {
      return <NoDataItem message={labels.selectCategoryFirst || t('reports.filters.selectCategoryFirst')} />;
    }
    
    if (middlePanelLoading) {
      return <NoDataItem message={t('common.loading')} />;
    }
    
    if (filteredMiddlePanelItems.length === 0) {
      return <NoDataItem message={labels.noItemsFound || t('reports.filters.noItemsFound')} />;
    }

    // Use grouped display if available
    if (groupedMiddlePanelItems) {
      return groupedMiddlePanelItems.map((group) => (
        <li key={group.groupName} className={styles.categoryGroup}>
          <div className={styles.categoryGroupHeader}>{group.groupName}</div>
          <ul className={styles.categoryGroupItems}>
            {group.items.map((item) => (
              <CheckboxItem
                key={getItemValue(item)}
                item={item}
                isSelected={selectedItemIds.includes(getItemValue(item))}
                instanceId={instanceId}
                getItemValue={getItemValue}
                onToggle={handleItemToggle}
              />
            ))}
          </ul>
        </li>
      ));
    }
    
    return filteredMiddlePanelItems.map((item) => (
      <CheckboxItem
        key={getItemValue(item)}
        item={item}
        isSelected={selectedItemIds.includes(getItemValue(item))}
        instanceId={instanceId}
        getItemValue={getItemValue}
        onToggle={handleItemToggle}
      />
    ));
  };

  // Determine grid class based on mode
  const gridClass = mode === LAYOUT_MODES.THREE_COLUMNS_WITH_FILTER
    ? styles.threePanelGrid
    : styles.dualPanelGrid;

  return (
    <div className={styles.container}>
      <div className={gridClass}>
        {/* Left Panel */}
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <h3>{labels.leftPanelTitle || t('reports.filters.availableEntities')}</h3>
          </div>
          {mode !== LAYOUT_MODES.TWO_COLUMNS && (
            <FilterDropdown
              filterOptions={filterOptions}
              selectedFilterValue={selectedFilterValue}
              onFilterChange={onFilterChange}
              label={labels.filterByLabel}
            />
          )}
          <PanelSearch
            value={leftSearchTerm}
            onChange={setLeftSearchTerm}
          />
          
          {mode === LAYOUT_MODES.THREE_COLUMNS_WITH_FILTER ? (
            <ul className={styles.categoryList}>
              {filteredLeftPanelItems.length === 0 ? (
                <NoDataItem message={labels.noCategoriesAvailable || t('reports.filters.noItemsAvailable')} />
              ) : (
                filteredLeftPanelItems.map((item) => (
                  <CategoryItem
                    key={getItemValue(item)}
                    item={item}
                    isSelected={selectedCategoryId === getItemValue(item)}
                    getItemValue={getItemValue}
                    onSelect={onCategorySelect}
                  />
                ))
              )}
            </ul>
          ) : (
            <>
              <div className={styles.panelActions}>
                {showSelectAll ? (
                  <button type="button" className={styles.actionLink} onClick={handleSelectAll}>
                    {labels.selectAll || t('common.selectAll')}
                  </button>
                ) : (
                  <span>&nbsp;</span>
                )}
              </div>
              <ul className={styles.itemList}>
                {filteredLeftPanelItems.length === 0 ? (
                  <NoDataItem
                    message={availableItems.length === 0
                      ? labels.noItemsAvailable || t('reports.filters.noItemsAvailable')
                      : labels.noItemsFound || t('reports.filters.noItemsFound')}
                  />
                ) : (
                  filteredLeftPanelItems.map((item) => (
                    <CheckboxItem
                      key={getItemValue(item)}
                      item={item}
                      isSelected={selectedItemIds.includes(getItemValue(item))}
                      instanceId={instanceId}
                      getItemValue={getItemValue}
                      onToggle={handleItemToggle}
                    />
                  ))
                )}
              </ul>
            </>
          )}
        </div>

        {/* Middle Panel (only for 3-column mode) */}
        {mode === LAYOUT_MODES.THREE_COLUMNS_WITH_FILTER && (
          <div className={styles.panel}>
            <div className={styles.panelHeader}>
              <h3>{labels.middlePanelTitle || t('reports.filters.select')}</h3>
            </div>
            <div className={styles.filterByLabelPlaceholder} />
            <PanelSearch
              value={middleSearchTerm}
              onChange={setMiddleSearchTerm}
              disabled={!selectedCategoryId}
            />
            <div className={styles.panelActions}>
              {showSelectAll ? (
                <button type="button" className={styles.actionLink} onClick={handleSelectAll}>
                  {labels.selectAll || t('common.selectAll')}
                </button>
              ) : (
                <span>&nbsp;</span>
              )}
            </div>
            <ul className={styles.itemList}>
              {renderMiddlePanelContent()}
            </ul>
          </div>
        )}

        {/* Arrow */}
        <PanelArrow />

        {/* Right Panel (Selected Items) */}
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <h3>{labels.rightPanelTitle || t('reports.filters.selectedEntities')} ({selectedItemIds.length})</h3>
          </div>
          {mode !== LAYOUT_MODES.TWO_COLUMNS && filterOptions && filterOptions.length > 0 && (
            <div className={styles.filterByLabelPlaceholder} />
          )}
          <PanelSearch
            value={selectedSearchTerm}
            onChange={setSelectedSearchTerm}
          />
          <div className={styles.panelActions}>
            {selectedItemIds.length > 0 ? (
              <button type="button" className={styles.actionLink} onClick={handleClearAll}>
                {labels.clearAll || t('common.clearAll')}
              </button>
            ) : (
              <span>&nbsp;</span>
            )}
          </div>
          <ul className={styles.itemList}>
            {renderSelectedPanelContent()}
          </ul>
        </div>
      </div>
    </div>
  );
}

EntityPanelSelector.propTypes = {
  mode: PropTypes.oneOf(Object.values(LAYOUT_MODES)).isRequired,
  availableItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  selectedItemIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ).isRequired,
  onSelectionChange: PropTypes.func.isRequired,
  middlePanelItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string.isRequired,
    })
  ),
  middlePanelLoading: PropTypes.bool,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string.isRequired,
    })
  ),
  selectedCategoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCategorySelect: PropTypes.func,
  selectedItemsWithCategory: PropTypes.objectOf(
    PropTypes.shape({
      item: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string.isRequired,
      }).isRequired,
      categoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      categoryName: PropTypes.string.isRequired,
    })
  ),
  filterOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  selectedFilterValue: PropTypes.string,
  onFilterChange: PropTypes.func,
  labels: PropTypes.shape({
    leftPanelTitle: PropTypes.string,
    middlePanelTitle: PropTypes.string,
    rightPanelTitle: PropTypes.string,
    filterByLabel: PropTypes.string,
    selectAll: PropTypes.string,
    clearAll: PropTypes.string,
    noItemsAvailable: PropTypes.string,
    noItemsFound: PropTypes.string,
    noItemsSelected: PropTypes.string,
    noCategoriesAvailable: PropTypes.string,
    selectCategoryFirst: PropTypes.string,
  }),
  valueField: PropTypes.oneOf(['id', 'dbId']),
};

EntityPanelSelector.defaultProps = {
  middlePanelItems: [],
  middlePanelLoading: false,
  categories: [],
  selectedCategoryId: null,
  onCategorySelect: null,
  selectedItemsWithCategory: null,
  filterOptions: [],
  selectedFilterValue: '',
  onFilterChange: null,
  labels: {},
  valueField: 'id',
};

export default EntityPanelSelector;
